import { App } from "antd";
import { type ReactNode, useMemo } from "react";

/**
 * Centralised notification helpers built on antd's App.useApp().
 * `success`/`error` use notification boxes at the bottom center; `info` (realtime events)
 * uses the lighter message pill, repositioned to bottom-center via the
 * `.ant-message` override in styles.css, and accepts rich content (e.g. a link
 * to the changed entity). Concurrent messages queue vertically.
 *
 * Memoized on the (stable) antd notification instance so the returned object keeps a stable
 * identity across renders. Without this, callers that put `notify` in a useEffect/useCallback
 * dependency array re-run every render — and any effect that also calls setState (e.g. setActions)
 * spins into an infinite render loop.
 */
export function useNotify() {
	const { notification, message } = App.useApp();

	return useMemo(
		() => ({
			success: (title: string, description?: string) =>
				notification.success({
					message: title,
					description,
					placement: "bottom",
				}),
			error: (title: string, description?: string) =>
				notification.error({
					message: title,
					description,
					placement: "bottom",
				}),
			info: (content: ReactNode) => message.info(content),
		}),
		[notification, message],
	);
}
